import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { PageHeader, Panel, StatTile, DataTable, Chip, EmptyState, Select, Button, Modal, FormRow, ConfirmDialog, Banner, useToast, type Column } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post } from '../lib/api'
import { money, num, dateShort } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

type Row = {
  buyer_id: string; buyer_name: string; contact_name: string | null; contact_email: string | null; buyer_status: string
  price_per_lead_cents: number | null; category_name: string | null
  leads: number; total_cents: number; unpriced: number
  status: 'invoiced' | 'paid' | null; invoiced_cents: number | null; invoiced_leads: number | null; invoiced_at: string | null; paid_at: string | null
  reference: string | null; note: string | null; changed_since_invoice: boolean
}
type Month = {
  period: string; current: string; closed: boolean; buyers: Row[]
  totals: { leads: number; billed_cents: number; unpriced: number; not_invoiced_cents: number; outstanding_cents: number; paid_cents: number; changed: number }
}

export const monthName = (p: string) => new Date(`${p}-15T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

type Action = { kind: 'invoice' | 'paid' | 'reopen'; row: Row }

export default function Billing() {
  const [params, setParams] = useSearchParams()
  const periods = useApi<{ current: string; periods: { period: string; leads: number; total_cents: number }[] }>('/api/billing/periods')
  const period = params.get('period') || periods.data?.current || ''
  const month = useApi<Month>(period ? `/api/billing?period=${period}` : null)
  const toast = useToast()
  const [action, setAction] = useState<Action | null>(null)
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)

  const act = async () => {
    if (!action) return
    setBusy(true)
    try {
      await post(`/api/billing/${action.row.buyer_id}/${period}/${action.kind}`, { reference: reference || undefined })
      toast(action.kind === 'invoice' ? 'Marked invoiced' : action.kind === 'paid' ? 'Marked paid' : 'Back to not invoiced', 'pos')
      setAction(null); setReference(''); month.reload(); periods.reload()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }

  const state = (r: Row) => r.status === 'paid'
    ? <Chip tone="pos" dot>Paid {r.paid_at ? dateShort(r.paid_at) : ''}</Chip>
    : r.status === 'invoiced' ? <Chip tone="accent" dot>Invoiced {r.invoiced_at ? dateShort(r.invoiced_at) : ''}</Chip>
    : <Chip dot>Not invoiced</Chip>

  const cols: Column<Row>[] = [
    { key: 'buyer', header: 'Buyer', cell: (r) => <><Link to={`/billing/${r.buyer_id}/${period}`}><strong>{r.buyer_name}</strong></Link><div className="muted nowrap">{[r.category_name, r.contact_email].filter(Boolean).join(' · ')}</div></>, sortBy: (r) => r.buyer_name },
    { key: 'leads', header: 'Leads', numeric: true, cell: (r) => <>{num(r.leads)}{r.unpriced ? <div className="tone-warn nowrap">{r.unpriced} no price</div> : null}</>, sortBy: (r) => r.leads },
    { key: 'total', header: 'Billed', numeric: true, cell: (r) => <strong>{money(r.total_cents)}</strong>, sortBy: (r) => r.total_cents },
    { key: 'state', header: 'Status', cell: (r) => <>{state(r)}{r.changed_since_invoice && <div className="tone-warn nowrap">Changed since invoiced: was {money(r.invoiced_cents)}</div>}{r.reference && <div className="muted nowrap">Ref {r.reference}</div>}</>, sortBy: (r) => r.status || '' },
    { key: 'act', header: '', cell: (r) => (
      <span className="row-tight nowrap">
        <Link className="btn btn-ghost" to={`/billing/${r.buyer_id}/${period}`}>Statement</Link>
        {r.status !== 'paid' && r.leads > 0 && <Button variant={r.status ? 'ghost' : 'secondary'} onClick={() => { setReference(r.reference || ''); setAction({ kind: 'invoice', row: r }) }}>{r.status ? 'Invoice again' : 'Mark invoiced'}</Button>}
        {r.status === 'invoiced' && <Button variant="secondary" onClick={() => { setReference(r.reference || ''); setAction({ kind: 'paid', row: r }) }}>Mark paid</Button>}
        {r.status && <Button variant="ghost" onClick={() => setAction({ kind: 'reopen', row: r })}>Reopen</Button>}
      </span>
    ) },
  ]

  const t = month.data?.totals
  return (
    <>
      <PageHeader title="Billing" subtitle="What each buyer owes for the leads sold to them, month by month. A lead is billed at the buyer's price when the rep replies."
                  actions={period && <a className="btn btn-secondary" href={`/api/billing/export.csv?period=${period}`}>Download CSV</a>} />
      {periods.error && <ErrorBlock error={periods.error} />}
      {periods.data && (
        <div className="lead-filters-row billing-month" style={{ marginBottom: 'var(--s-6)' }}>
          <Select aria-label="Month" value={period} onChange={(e) => setParams({ period: e.target.value }, { replace: true })}>
            {periods.data.periods.map((p) => <option key={p.period} value={p.period}>{monthName(p.period)}{p.period === periods.data!.current ? ' (this month)' : ''}{p.leads ? ` · ${p.leads} leads` : ''}</option>)}
          </Select>
          {month.data && !month.data.closed && <span className="muted">This month is still running: sales keep landing in it until it ends.</span>}
        </div>
      )}
      {month.error && <ErrorBlock error={month.error} />}
      {!month.data ? (period ? <LoadingBlock /> : null) : (
        <>
          <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
            <StatTile focal label="Billed" value={money(t!.billed_cents)} meta={`${num(t!.leads)} leads to ${month.data.buyers.length} buyers`} />
            <StatTile label="Not invoiced yet" value={money(t!.not_invoiced_cents)} meta={t!.unpriced ? `${t!.unpriced} sold with no price set` : 'every sale has a price'} />
            <StatTile label="Invoiced, unpaid" value={money(t!.outstanding_cents)} meta="waiting on buyers" />
            <StatTile label="Paid" value={money(t!.paid_cents)} meta={t!.billed_cents ? `${Math.round((t!.paid_cents / t!.billed_cents) * 100)}% of billed` : 'nothing billed'} progress={t!.billed_cents ? (t!.paid_cents / t!.billed_cents) * 100 : undefined} />
          </div>
          {t!.changed > 0 && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="warn" title={`${t!.changed} invoice${t!.changed === 1 ? '' : 's'} no longer match the month`}>A sale landed after it was invoiced. Invoice again to send the new total, or bill the difference next month.</Banner></div>}
          {t!.unpriced > 0 && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="warn" title="Some sales have no price">Those buyers had no price per lead set when the rep replied, so the lead was delivered at $0. Set prices on the Subscribers page; past sales keep the price they had.</Banner></div>}
          <Panel className="panel-flush">
            <DataTable columns={cols} rows={month.data.buyers} rowKey={(r) => r.buyer_id}
                       empty={<EmptyState title={`Nothing sold in ${monthName(period)}`} body="A lead is billed when a rep replies and recommends the buyer." />} />
          </Panel>
        </>
      )}

      {action && action.kind !== 'reopen' && (
        <Modal title={action.kind === 'invoice' ? `Invoice ${action.row.buyer_name}` : `Record payment from ${action.row.buyer_name}`} onClose={() => setAction(null)}
               footer={<><Button variant="secondary" onClick={() => setAction(null)}>Cancel</Button><Button onClick={act} disabled={busy}>{action.kind === 'invoice' ? `Mark ${money(action.row.total_cents)} invoiced` : 'Mark paid'}</Button></>}>
          <div className="stack-tight">
            <p style={{ margin: 0 }}>{action.kind === 'invoice'
              ? <>{num(action.row.leads)} leads in {monthName(period)} for {money(action.row.total_cents)}. This records that you invoiced it; send the invoice from your usual tool.</>
              : <>{money(action.row.invoiced_cents)} invoiced for {monthName(period)}.</>}</p>
            <FormRow label={action.kind === 'invoice' ? 'Invoice number (optional)' : 'Payment reference (optional)'} htmlFor="bill-ref">
              <input id="bill-ref" className="field" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={100} />
            </FormRow>
          </div>
        </Modal>
      )}
      {action?.kind === 'reopen' && (
        <ConfirmDialog title={`Reopen ${action.row.buyer_name} for ${monthName(period)}?`} tone="neg" confirmLabel="Reopen"
                       body={`This clears the ${action.row.status} mark and its reference. The sales themselves are not touched.`}
                       onConfirm={act} onCancel={() => setAction(null)} />
      )}
    </>
  )
}

type Statement = {
  org: { name: string; email_reply_to: string | null }
  buyer: { id: string; name: string; contact_name: string | null; contact_email: string | null; contact_phone: string | null; category_name: string | null }
  period: string; timezone: string
  lines: { id: string; billed_at: string; billed_cents: number | null; post_id: string; summary: string | null; category_name: string | null; area_name: string | null; group_name: string | null; rep_name: string }[]
  totals: { leads: number; total_cents: number; unpriced: number }
  statement: { status: string; invoiced_cents: number; invoiced_at: string; paid_at: string | null; reference: string | null } | null
  changed_since_invoice: boolean
}

/** One buyer's month, laid out to print or save as PDF. No poster, no post words, no rep names on the printed copy. */
export function StatementPage() {
  const { buyer, period } = useParams()
  const { data, error } = useApi<Statement>(`/api/billing/${buyer}/${period}`)
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const dt = (d: string) => new Date(d).toLocaleDateString('en-US', { timeZone: data.timezone, month: 'short', day: 'numeric' })
  return (
    <>
      <div className="no-print" style={{ marginBottom: 'var(--s-6)' }}>
        <div className="row-tight">
          <Link className="btn btn-ghost" to={`/billing?period=${period}`}>Back to billing</Link>
          <Button onClick={() => window.print()}>Print or save as PDF</Button>
        </div>
        {data.changed_since_invoice && <div style={{ marginTop: 'var(--s-4)' }}><Banner tone="warn" title="Changed since invoiced">Invoiced for {money(data.statement!.invoiced_cents)}; the month now totals {money(data.totals.total_cents)}.</Banner></div>}
      </div>
      <article className="statement panel">
        <header className="statement-head">
          <div>
            <p className="muted statement-kicker">Statement</p>
            <h1 className="statement-title">{data.org.name}</h1>
            {data.org.email_reply_to && <p className="muted">{data.org.email_reply_to}</p>}
          </div>
          <div className="statement-meta">
            <p><strong>{monthName(data.period)}</strong></p>
            {data.statement?.reference && <p className="muted">Ref {data.statement.reference}</p>}
            {data.statement && <p className="muted">{data.statement.status === 'paid' ? `Paid ${dateShort(data.statement.paid_at)}` : `Invoiced ${dateShort(data.statement.invoiced_at)}`}</p>}
          </div>
        </header>
        <section className="statement-to">
          <p className="muted statement-kicker">For</p>
          <p><strong>{data.buyer.name}</strong></p>
          {data.buyer.contact_name && <p>{data.buyer.contact_name}</p>}
          {data.buyer.contact_email && <p className="muted">{data.buyer.contact_email}</p>}
        </section>
        {data.lines.length === 0 ? <EmptyState title="No leads this month" /> : (
          <table className="statement-table">
            <thead><tr><th>Date</th><th>Request</th><th>Area</th><th className="num">Price</th></tr></thead>
            <tbody>
              {data.lines.map((l) => (
                <tr key={l.id}>
                  <td className="nowrap">{dt(l.billed_at)}</td>
                  <td>{l.summary || 'Recommendation'}{l.category_name ? <span className="muted"> · {l.category_name}</span> : null}</td>
                  <td>{l.area_name || <span className="muted">not stated</span>}</td>
                  <td className="num">{l.billed_cents != null ? money(l.billed_cents) : <span className="muted">no charge</span>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={3}><strong>{num(data.totals.leads)} lead{data.totals.leads === 1 ? '' : 's'}</strong></td><td className="num"><strong>{money(data.totals.total_cents)}</strong></td></tr></tfoot>
          </table>
        )}
        <p className="muted statement-foot">Each line is a recommendation made to someone asking for this service. Dated when the recommendation was made.</p>
      </article>
      <p className="muted no-print" style={{ marginTop: 'var(--s-4)' }}>Printed without the rep's name. Admins see who carried each lead on <Link to={`/activity?tab=sold&buyer=${data.buyer.id}&days=all`}>Activity</Link>.</p>
    </>
  )
}
